import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Box,
  Typography,
  Chip,
} from '@mui/material';
import { useLanguage } from '../../contexts/LanguageContext';
import payrollsService from '../../services/payrolls.service';
import { Payroll, PayrollDetail } from '../../types/payrolls.types';
import { toast } from 'react-toastify';

const translations = {
  en: {
    title: 'Payroll Details',
    employeeCode: 'Code',
    employeeName: 'Employee',
    basicSalary: 'Basic',
    housingAllowance: 'Housing',
    transportAllowance: 'Transport',
    otherAllowances: 'Other',
    overtimePay: 'Overtime',
    publicHolidayPay: 'Holiday',
    offDayPay: 'Off Day',
    totalEarnings: 'Total Earnings',
    absences: 'Absences',
    penalties: 'Penalties',
    otherDeductions: 'Other Ded.',
    totalDeductions: 'Total Ded.',
    netSalary: 'Net Salary',
    bankAmount: 'Bank',
    cashAmount: 'Cash',
    paymentMethod: 'Payment',
    close: 'Close',
    loading: 'Loading...',
    error: 'Failed to load details',
    kwd: 'KWD',
    bank: 'Bank',
    cash: 'Cash',
    check: 'Check',
    total: 'Total',
    employees: 'Employees',
  },
  ar: {
    title: 'تفاصيل الراتب',
    employeeCode: 'الكود',
    employeeName: 'الموظف',
    basicSalary: 'الأساسي',
    housingAllowance: 'السكن',
    transportAllowance: 'النقل',
    otherAllowances: 'أخرى',
    overtimePay: 'إضافي',
    publicHolidayPay: 'عطل',
    offDayPay: 'كانسل أوف',
    totalEarnings: 'إجمالي الاستحقاقات',
    absences: 'غياب',
    penalties: 'جزاءات',
    otherDeductions: 'خصومات أخرى',
    totalDeductions: 'إجمالي الخصومات',
    netSalary: 'الصافي',
    bankAmount: 'بنك',
    cashAmount: 'نقد',
    paymentMethod: 'الدفع',
    close: 'إغلاق',
    loading: 'جاري التحميل...',
    error: 'فشل تحميل التفاصيل',
    kwd: 'د.ك',
    bank: 'بنك',
    cash: 'نقد',
    check: 'شيك',
    total: 'الإجمالي',
    employees: 'موظفين',
  },
};

interface PayrollDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  payroll: Payroll;
}

const PayrollDetailsDialog: React.FC<PayrollDetailsDialogProps> = ({ open, onClose, payroll }) => {
  const { language } = useLanguage();
  const t = translations[language];
  const isRTL = language === 'ar';

  const [details, setDetails] = useState<PayrollDetail[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      loadDetails();
    }
  }, [open, payroll.payrollId]);

  const loadDetails = async () => {
    setLoading(true);
    try {
      const data = await payrollsService.getDetails(payroll.payrollId);
      setDetails(data);
    } catch (error) {
      console.error('Failed to load payroll details:', error);
      toast.error(t.error);
    } finally {
      setLoading(false);
    }
  };

  const getPaymentMethodLabel = (method: string) => {
    switch (method) {
      case 'BankTransfer': return t.bank;
      case 'Cash': return t.cash;
      case 'Check': return t.check;
      default: return method;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">{t.title}</Typography>
          <Typography variant="subtitle1" color="textSecondary">
            {payroll.payrollNumber}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>{t.employeeCode}</strong></TableCell>
                  <TableCell><strong>{t.employeeName}</strong></TableCell>
                  <TableCell align="right"><strong>{t.basicSalary}</strong></TableCell>
                  <TableCell align="right"><strong>{t.housingAllowance}</strong></TableCell>
                  <TableCell align="right"><strong>{t.transportAllowance}</strong></TableCell>
                  <TableCell align="right"><strong>{t.otherAllowances}</strong></TableCell>
                  <TableCell align="right"><strong>{t.overtimePay}</strong></TableCell>
                  <TableCell align="right"><strong>{t.publicHolidayPay}</strong></TableCell>
                  <TableCell align="right"><strong>{t.offDayPay}</strong></TableCell>
                  <TableCell align="right"><strong>{t.totalEarnings}</strong></TableCell>
                  <TableCell align="right"><strong>{t.absences}</strong></TableCell>
                  <TableCell align="right"><strong>{t.penalties}</strong></TableCell>
                  <TableCell align="right"><strong>{t.otherDeductions}</strong></TableCell>
                  <TableCell align="right"><strong>{t.totalDeductions}</strong></TableCell>
                  <TableCell align="right"><strong>{t.netSalary}</strong></TableCell>
                  <TableCell align="right"><strong>{t.bankAmount}</strong></TableCell>
                  <TableCell align="right"><strong>{t.cashAmount}</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {details.map((detail) => (
                  <TableRow key={detail.payrollDetailId} hover>
                    <TableCell>{detail.employeeCode}</TableCell>
                    <TableCell>{isRTL ? detail.employeeNameAr : detail.employeeName}</TableCell>
                    <TableCell align="right">{detail.basicSalary.toLocaleString('en-GB', { minimumFractionDigits: 3 })}</TableCell>
                    <TableCell align="right">{detail.housingAllowance.toLocaleString('en-GB', { minimumFractionDigits: 3 })}</TableCell>
                    <TableCell align="right">{detail.transportAllowance.toLocaleString('en-GB', { minimumFractionDigits: 3 })}</TableCell>
                    <TableCell align="right">{detail.otherAllowances.toLocaleString('en-GB', { minimumFractionDigits: 3 })}</TableCell>
                    <TableCell align="right">{detail.overtimePay.toLocaleString('en-GB', { minimumFractionDigits: 3 })}</TableCell>
                    <TableCell align="right">{detail.publicHolidayPay.toLocaleString('en-GB', { minimumFractionDigits: 3 })}</TableCell>
                    <TableCell align="right">{(detail.offDayPay || 0).toLocaleString('en-GB', { minimumFractionDigits: 3 })}</TableCell>
                    <TableCell align="right">
                      <strong>{detail.totalEarnings.toLocaleString('en-GB', { minimumFractionDigits: 3 })}</strong>
                    </TableCell>
                    <TableCell align="right" sx={{ color: 'error.main' }}>
                      {detail.absences.toLocaleString('en-GB', { minimumFractionDigits: 3 })}
                    </TableCell>
                    <TableCell align="right" sx={{ color: 'error.main' }}>
                      {detail.penalties.toLocaleString('en-GB', { minimumFractionDigits: 3 })}
                    </TableCell>
                    <TableCell align="right" sx={{ color: 'error.main' }}>
                      {detail.otherDeductions.toLocaleString('en-GB', { minimumFractionDigits: 3 })}
                    </TableCell>
                    <TableCell align="right">
                      <Box component="span" sx={{ color: 'error.main', fontWeight: 'bold' }}>
                        {detail.totalDeductions.toLocaleString('en-GB', { minimumFractionDigits: 3 })}
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Chip 
                        label={detail.netSalary.toLocaleString('en-GB', { minimumFractionDigits: 3 })} 
                        color="success" 
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Chip 
                        label={detail.bankAmount.toLocaleString('en-GB', { minimumFractionDigits: 3 })} 
                        color="primary" 
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Chip 
                        label={detail.cashAmount.toLocaleString('en-GB', { minimumFractionDigits: 3 })} 
                        color="warning" 
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
              {details.length > 0 && (
                <TableFooter>
                  <TableRow sx={{ bgcolor: '#1a237e', '& td': { color: 'white', fontWeight: 700, fontSize: '0.8rem', borderTop: '2px solid #fff' } }}>
                    <TableCell colSpan={2} sx={{ color: 'white !important' }}>
                      <strong>{t.total} ({details.length} {t.employees})</strong>
                    </TableCell>
                    <TableCell align="right">{details.reduce((s, d) => s + d.basicSalary, 0).toLocaleString('en-GB', { minimumFractionDigits: 3 })}</TableCell>
                    <TableCell align="right">{details.reduce((s, d) => s + d.housingAllowance, 0).toLocaleString('en-GB', { minimumFractionDigits: 3 })}</TableCell>
                    <TableCell align="right">{details.reduce((s, d) => s + d.transportAllowance, 0).toLocaleString('en-GB', { minimumFractionDigits: 3 })}</TableCell>
                    <TableCell align="right">{details.reduce((s, d) => s + d.otherAllowances, 0).toLocaleString('en-GB', { minimumFractionDigits: 3 })}</TableCell>
                    <TableCell align="right">{details.reduce((s, d) => s + d.overtimePay, 0).toLocaleString('en-GB', { minimumFractionDigits: 3 })}</TableCell>
                    <TableCell align="right">{details.reduce((s, d) => s + d.publicHolidayPay, 0).toLocaleString('en-GB', { minimumFractionDigits: 3 })}</TableCell>
                    <TableCell align="right">{details.reduce((s, d) => s + (d.offDayPay || 0), 0).toLocaleString('en-GB', { minimumFractionDigits: 3 })}</TableCell>
                    <TableCell align="right" sx={{ bgcolor: '#0d47a1 !important' }}>{details.reduce((s, d) => s + d.totalEarnings, 0).toLocaleString('en-GB', { minimumFractionDigits: 3 })}</TableCell>
                    <TableCell align="right" sx={{ color: '#ff8a80 !important' }}>{details.reduce((s, d) => s + d.absences, 0).toLocaleString('en-GB', { minimumFractionDigits: 3 })}</TableCell>
                    <TableCell align="right" sx={{ color: '#ff8a80 !important' }}>{details.reduce((s, d) => s + d.penalties, 0).toLocaleString('en-GB', { minimumFractionDigits: 3 })}</TableCell>
                    <TableCell align="right" sx={{ color: '#ff8a80 !important' }}>{details.reduce((s, d) => s + d.otherDeductions, 0).toLocaleString('en-GB', { minimumFractionDigits: 3 })}</TableCell>
                    <TableCell align="right" sx={{ color: '#ff5252 !important' }}>{details.reduce((s, d) => s + d.totalDeductions, 0).toLocaleString('en-GB', { minimumFractionDigits: 3 })}</TableCell>
                    <TableCell align="right" sx={{ bgcolor: '#1b5e20 !important', fontSize: '0.85rem !important' }}>{details.reduce((s, d) => s + d.netSalary, 0).toLocaleString('en-GB', { minimumFractionDigits: 3 })}</TableCell>
                    <TableCell align="right" sx={{ bgcolor: '#0d47a1 !important' }}>{details.reduce((s, d) => s + d.bankAmount, 0).toLocaleString('en-GB', { minimumFractionDigits: 3 })}</TableCell>
                    <TableCell align="right" sx={{ bgcolor: '#e65100 !important' }}>{details.reduce((s, d) => s + d.cashAmount, 0).toLocaleString('en-GB', { minimumFractionDigits: 3 })}</TableCell>
                  </TableRow>
                </TableFooter>
              )}
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">{t.close}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default PayrollDetailsDialog;